import { useSyncExternalStore } from 'react';
import { api } from '../api';
import { DeliveryMeasurement } from './measurement';
export type AdPlacement = 'subway' | 'cafe' | 'journal' | 'community';
export type AdAspect = '16:9' | '4:3' | '1:1';
export const adAspectRatio = (aspect: AdAspect) => (aspect === '4:3' ? 4 / 3 : aspect === '1:1' ? 1 : 16 / 9);
export type AdContext = { actorId: string; cityId: string; zone: string; projectId?: string };
export type AdCampaign = {
  id: string;
  sponsor: string;
  title: string;
  body: string;
  placement: AdPlacement;
  aspect: AdAspect;
  creativeHash: string;
  creativeRevision: number;
  assetId: string | null;
  assetAlt: string;
  assetPath: string | null;
  clickPath: string;
  deliveryToken: string;
  deliveryExpiresAt: string;
  ends_at: string;
};
export type AdDelivery = { campaign: AdCampaign; measurement: DeliveryMeasurement; expiresAt: number };
type Entry = {
  value: AdDelivery | null;
  subscribe: (listener: () => void) => () => void;
  read: () => AdDelivery | null;
};
const entries = new Map<string, Entry>();
const empty = () => null;
const noSubscribe = () => () => {};
export const safeAssetPath = (path: string | null | undefined) =>
  typeof path === 'string' && /^\/api\/ads\/assets\/[0-9a-f-]{36}$/i.test(path) ? path : null;
export const safeClickPath = (path: string) =>
  /^\/api\/ads\/click\/[0-9a-f-]{36}\?delivery=[A-Za-z0-9_.%-]+$/i.test(path);
function entryFor(key: string, placement: AdPlacement) {
  const existing = entries.get(key);
  if (existing) return existing;
  const listeners = new Set<() => void>();
  let timer: ReturnType<typeof setInterval> | undefined;
  let retire: ReturnType<typeof setTimeout> | undefined;
  let expiry: ReturnType<typeof setTimeout> | undefined;
  let controller: AbortController | undefined;
  let removed = false;
  const publish = (value: AdDelivery | null) => {
    entry.value?.measurement.dispose();
    entry.value = value;
    if (expiry) clearTimeout(expiry);
    if (value) expiry = setTimeout(() => publish(null), Math.max(0, value.expiresAt - performance.now()));
    for (const listener of listeners) listener();
  };
  const pause = () => entry.value?.measurement.pause();
  const refresh = () => {
    if (removed || !listeners.size || document.visibilityState !== 'visible' || controller) return;
    controller = new AbortController();
    const current = controller;
    const startedAt = performance.now();
    api<{ campaign: AdCampaign | null; serverTime: string }>(`/ads/${placement}`, {
      signal: AbortSignal.any([current.signal, AbortSignal.timeout(5000)]),
    })
      .then((response) => {
        if (removed || current.signal.aborted) return;
        const campaign = response.campaign,
          serverNow = Date.parse(response.serverTime);
        const receivedAt = performance.now();
        if (
          !campaign ||
          !safeClickPath(campaign.clickPath) ||
          typeof campaign.deliveryToken !== 'string' ||
          !Number.isFinite(serverNow)
        ) {
          publish(null);
          return;
        }
        const remaining =
          Math.min(Date.parse(campaign.deliveryExpiresAt), Date.parse(campaign.ends_at)) -
          serverNow -
          (receivedAt - startedAt);
        if (!Number.isFinite(remaining) || remaining <= 0) {
          publish(null);
          return;
        }
        const measurement = new DeliveryMeasurement(receivedAt, (event, signal) =>
          api('/ads/track', {
            method: 'POST',
            body: JSON.stringify({ id: campaign.id, event, delivery: campaign.deliveryToken }),
            signal,
          }),
        );
        publish({ campaign, measurement, expiresAt: receivedAt + remaining });
      })
      .catch(() => {
        if (!removed && !current.signal.aborted) publish(null);
      })
      .finally(() => {
        if (controller === current) controller = undefined;
      });
  };
  const visibility = () => {
    pause();
    if (document.visibilityState === 'visible') refresh();
  };
  const entry: Entry = {
    value: null,
    read: () => entry.value,
    subscribe(listener) {
      listeners.add(listener);
      if (retire) {
        clearTimeout(retire);
        retire = undefined;
      }
      if (!timer) {
        refresh();
        timer = setInterval(refresh, 30000);
        document.addEventListener('visibilitychange', visibility);
        window.addEventListener('ado-ads-changed', refresh);
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size) return;
        retire = setTimeout(() => {
          if (listeners.size) return;
          removed = true;
          controller?.abort();
          if (timer) clearInterval(timer);
          if (expiry) clearTimeout(expiry);
          document.removeEventListener('visibilitychange', visibility);
          window.removeEventListener('ado-ads-changed', refresh);
          entry.value?.measurement.dispose();
          entries.delete(key);
        }, 0);
      };
    },
  };
  entries.set(key, entry);
  return entry;
}
export function useAdDelivery(placement: AdPlacement, context?: AdContext) {
  const key = context
    ? JSON.stringify([
        context.actorId,
        context.cityId,
        context.zone,
        placement,
        placement === 'community' ? context.projectId : null,
      ])
    : undefined;
  const entry = key ? entryFor(key, placement) : undefined;
  return useSyncExternalStore(entry?.subscribe || noSubscribe, entry?.read || empty, empty);
}
